import React, { useEffect, useRef, useState } from 'react';
import { loadEpigraphAR } from '../utils/epigraphLoader';

type EpigraphButtonElement = HTMLElement & {
    updateComplete?: Promise<boolean>;
    api?: { launchAR: () => Promise<unknown> };
};

interface Props {
    sku: string;
    gaMeasurementId?: string;
    children: React.ReactNode;
}

// Keep unrequested buttons out of the SDK, even when another viewer loads it.
const LazyEpigraphButton: React.FC<Props> = ({
    sku,
    gaMeasurementId,
    children,
}) => {
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(false);
    const buttonRef = useRef<EpigraphButtonElement | null>(null);

    useEffect(() => {
        if (!loading) return;
        let active = true;
        loadEpigraphAR()
            .then(() => {
                if (active) setReady(true);
            })
            .catch(() => {
                if (active) {
                    setLoading(false);
                    setError(true);
                }
            });
        return () => {
            active = false;
        };
    }, [loading]);

    useEffect(() => {
        if (!ready) return;
        let active = true;
        const button = buttonRef.current;
        // The SDK queues AR until its product data is ready.
        void Promise.resolve(button?.updateComplete)
            .then(() => {
                if (!active) return;
                if (!button?.api) throw new Error('AR viewer is unavailable');
                return button.api.launchAR();
            })
            .catch(() => {
                if (active) {
                    setReady(false);
                    setLoading(false);
                    setError(true);
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [ready]);

    return (
        <div style={{ width: '100%', maxWidth: 350 }} aria-busy={loading}>
            {ready ? (
                <epigraph-button
                    ref={buttonRef}
                    sku={sku}
                    ga-measurement-id={gaMeasurementId}
                >
                    {children}
                </epigraph-button>
            ) : (
                <div
                    onClickCapture={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setError(false);
                        setLoading(true);
                    }}
                >
                    {children}
                </div>
            )}
            {loading && <span role="status">Loading AR viewer...</span>}
            {error && (
                <span role="status">
                    Unable to load AR viewer. Select the button to try again.
                </span>
            )}
        </div>
    );
};

export default LazyEpigraphButton;
